import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { useWishlist } from '../../context/WishlistContext';
import { categories as categoriesApi } from '../../lib/api';
import { ShoppingCart, User, Menu, X, LogOut, Package, LayoutDashboard, Zap, Heart, CreditCard } from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

const Header = () => {
  const { user, isAuthenticated, isAdmin, logout } = useAuth();
  const { itemCount } = useCart();
  const { count: wishlistCount } = useWishlist();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [categoryLinks, setCategoryLinks] = useState([]);

  // Shorten category names for the nav bar
  const shortName = (name) => {
    return name
      .replace(/ Unisex$/i, '')
      .replace(/^Tote Bags$/i, 'Bags')
      .replace(/^Kids Clothing$/i, 'Kids')
      .replace(/^T-Shirt Lady Fit$/i, 'Lady Fit')
      .replace(/^Zip Hoodie$/i, 'Zipper');
  };

  // Fixed display order - new categories appear after Zipper
  const categoryOrder = [
    't-shirts', 'Tshirt Ladyfit', 'hoodies', 'sweatshirts', 'zip hoodie',
    // --- new categories go here ---
    'caps', 'bags', 'boxers', 'shorts', 'hotpants', 'kids'
  ];

  const sortCategories = (cats) => {
    const known = [];
    const unknown = [];
    for (const cat of cats) {
      const idx = categoryOrder.indexOf(cat.slug);
      if (idx !== -1) {
        known.push({ ...cat, _order: idx });
      } else {
        unknown.push(cat);
      }
    }
    known.sort((a, b) => a._order - b._order);
    // Insert unknown categories after Zipper (index 3) position
    const zipperIdx = known.findIndex(c => c.slug === 'zip hoodie');
    const insertAt = zipperIdx !== -1 ? zipperIdx + 1 : known.length;
    const result = [...known.slice(0, insertAt), ...unknown, ...known.slice(insertAt)];
    return result;
  };

  useEffect(() => {
    if (isAuthenticated) {
      categoriesApi.getAll().then(res => {
        const sorted = sortCategories(res.data || []);
        const cats = sorted.map(cat => ({
          name: shortName(cat.name),
          href: `/products?category=${encodeURIComponent(cat.slug)}`,
        }));
        setCategoryLinks(cats);
      }).catch(() => {});
    }
  }, [isAuthenticated]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navLinks = isAuthenticated ? [
    { name: 'Products', href: '/products' },
    { name: 'Quick Order', href: '/quick-order', highlight: true },
  ] : [
    { name: 'Products', href: '/products' },
  ];

  return (
    <header className="bg-white border-b border-[#E2E8F0] sticky top-0 z-50">
      <div className="container-wide">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img 
              src="https://irishconnexxion.com.au/wp-content/uploads/2021/06/Asset-1@3x.png" 
              alt="Irish Connexxion" 
              className="h-10 md:h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className={`text-sm font-medium transition-colors hover:text-[#002FA7] ${
                  link.highlight ? 'bg-[#002FA7] text-white px-3 py-1.5 hover:bg-[#0025a0] hover:text-white' :
                  location.pathname === link.href ? 'text-[#002FA7]' : 'text-[#64748B]'
                }`}
                data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
              >
                {link.highlight && <Zap className="w-3 h-3 inline mr-1" />}
                {link.name}
              </Link>
            ))}
            
            {/* Categories Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="text-sm font-medium text-[#64748B] hover:text-[#002FA7] transition-colors flex items-center gap-1">
                  Categories
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48">
                {categoryLinks.map((cat) => (
                  <DropdownMenuItem key={cat.name} asChild>
                    <Link to={cat.href} className="cursor-pointer">
                      {cat.name}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-4">
            {isAuthenticated ? (
              <>
                {/* Wishlist */}
                <Link
                  to="/wishlist"
                  className="relative p-2 hover:bg-[#F1F5F9] transition-colors"
                  data-testid="wishlist-link"
                >
                  <Heart className="w-5 h-5 text-[#0F172A]" />
                  {wishlistCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 flex items-center justify-center font-medium rounded-full">
                      {wishlistCount}
                    </span>
                  )}
                </Link>

                {/* Cart */}
                <Link
                  to="/cart"
                  className="relative p-2 hover:bg-[#F1F5F9] transition-colors"
                  data-testid="cart-link"
                >
                  <ShoppingCart className="w-5 h-5 text-[#0F172A]" />
                  {itemCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-[#002FA7] text-white text-xs w-5 h-5 flex items-center justify-center font-medium">
                      {itemCount}
                    </span>
                  )}
                </Link>

                {/* User Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="p-2" data-testid="user-menu-trigger">
                      <User className="w-5 h-5 text-[#0F172A]" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="px-3 py-2">
                      <p className="text-sm font-medium text-[#0F172A]">{user?.contact_name}</p>
                      <p className="text-xs text-[#64748B]">{user?.company_name}</p>
                    </div>
                    <DropdownMenuSeparator />
                    {isAdmin ? (
                      <DropdownMenuItem asChild>
                        <Link to="/admin" className="flex items-center gap-2" data-testid="admin-link">
                          <LayoutDashboard className="w-4 h-4" />
                          Admin Dashboard
                        </Link>
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem asChild>
                        <Link to="/dashboard" className="flex items-center gap-2" data-testid="dashboard-link">
                          <LayoutDashboard className="w-4 h-4" />
                          My Dashboard
                        </Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem asChild>
                      <Link to="/orders" className="flex items-center gap-2" data-testid="orders-link">
                        <Package className="w-4 h-4" />
                        My Orders
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/wishlist" className="flex items-center gap-2" data-testid="wishlist-menu-link">
                        <Heart className="w-4 h-4" />
                        My Wishlist
                        {wishlistCount > 0 && (
                          <span className="ml-auto text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full">
                            {wishlistCount}
                          </span>
                        )}
                      </Link>
                    </DropdownMenuItem>
                    {!isAdmin && (
                      <DropdownMenuItem asChild>
                        <Link
                          to="/credit-application"
                          className="flex items-center gap-2"
                          data-testid="apply-credit-link"
                        >
                          <CreditCard className="w-4 h-4" />
                          Apply for Credit Account
                        </Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="flex items-center gap-2 text-red-600" data-testid="logout-btn">
                      <LogOut className="w-4 h-4" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <div className="flex items-center gap-3">
                <Link to="/login">
                  <Button variant="ghost" className="text-sm font-medium" data-testid="login-link">
                    Login
                  </Button>
                </Link>
                <Link to="/register">
                  <Button className="bg-[#002FA7] hover:bg-[#0025a0] text-white text-sm font-medium px-4 py-2" data-testid="register-link">
                    Register
                  </Button>
                </Link>
              </div>
            )}

            {/* Mobile Menu Toggle */}
            <button
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Category Bar - Desktop */}
        <div className="hidden md:block border-t border-[#E2E8F0] bg-[#F8FAFC] overflow-x-auto">
          <div className="flex items-center justify-center gap-1 py-2 whitespace-nowrap">
            <Link
              to="/products"
              className="px-4 py-1.5 text-sm font-medium text-[#0F172A] hover:text-[#002FA7] hover:bg-white rounded transition-colors"
            >
              All Products
            </Link>
            <span className="text-[#E2E8F0]">|</span>
            {categoryLinks.map((cat, index) => (
              <React.Fragment key={cat.name + index}>
                <Link
                  to={cat.href}
                  className="px-4 py-1.5 text-sm text-[#64748B] hover:text-[#002FA7] hover:bg-white rounded transition-colors"
                >
                  {cat.name}
                </Link>
                {index < categoryLinks.length - 1 && <span className="text-[#E2E8F0]">|</span>}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-[#E2E8F0] py-4">
            <nav className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  className="px-4 py-2 text-sm font-medium text-[#64748B] hover:bg-[#F1F5F9] hover:text-[#002FA7]"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.name}
                </Link>
              ))}
              <div className="border-t border-[#E2E8F0] mt-2 pt-2">
                <p className="px-4 py-1 text-xs font-semibold text-[#94A3B8] uppercase">Categories</p>
                {categoryLinks.map((cat) => (
                  <Link
                    key={cat.name}
                    to={cat.href}
                    className="px-4 py-2 text-sm text-[#64748B] hover:bg-[#F1F5F9] hover:text-[#002FA7] block"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
